import { faBell } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { useState } from 'react';
import { Image, Modal, Platform, ScrollView, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import useCustomTheme from '../../../hooks/theme-hook';
import useTranslation from '../../../hooks/translation-hook';
import CustomButton from '../CustomButton';
import CustomCheckbox from '../CustomCheckbox';
import Label from '../Label';
import Overlay from '../Overlay';
import Popup from '../Popup';

export interface NoNearbyMissionsPopupProps {
  visible: boolean;
  onClose?(dontShowAgain?: boolean): void;
}

const heroImage = require('../../../../assets/no-nearby-missions-popup.png');
const heroImageAspectRatio = 623 / 414;

const NoNearbyMissionsPopup = (props: NoNearbyMissionsPopupProps) => {
  const t = useTranslation();
  const theme = useCustomTheme();
  const layout = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const nativeBottomInset = Platform.OS === 'android' ? Math.max(insets.bottom, 0) : 0;
  const popupWidth = Math.min(390, Math.max(layout.width - 30, 300));
  const popupMaxHeight = Math.max(layout.height - 40 - nativeBottomInset, 280);
  const heroHeight = Math.round(popupWidth / heroImageAspectRatio);
  const isCompact = popupWidth < 340;
  const isDarkMode = !!theme.isDark;
  const cardBackgroundColor = isDarkMode ? theme.background.modal.toString() : '#ffffff';
  const titleColor = isDarkMode ? theme.text.darkTurquoiseTwo.toString() : '#43a49e';
  const bodyColor = isDarkMode ? theme.text.primary.toString() : '#035164';
  const secondaryTextColor = isDarkMode ? theme.text.primary.toString() : '#035164';
  const buttonColor = isDarkMode ? theme.text.darkTurquoiseTwo.toString() : '#43a49e';

  const handleClose = () => {
    props.onClose?.(dontShowAgain);
    setDontShowAgain(false);
  };

  if (!props.visible) {
    return null;
  }

  return (
    <Modal animationType="fade" visible={props.visible} transparent={true} onRequestClose={handleClose} statusBarTranslucent>
      <Overlay showOverLay={props.visible} zIndex={3000} onPress={handleClose}>
        <Popup scrollable={false} backgroundColor={cardBackgroundColor} borderRadius={10}>
          <ScrollView
            style={{ flexGrow: 0, width: popupWidth, maxHeight: popupMaxHeight, backgroundColor: cardBackgroundColor }}
            contentContainerStyle={{ backgroundColor: cardBackgroundColor }}
            nestedScrollEnabled={true}
            bounces={false}
            alwaysBounceVertical={false}
            alwaysBounceHorizontal={false}
            overScrollMode="never"
          >
            <Image source={heroImage} style={{ width: popupWidth, height: heroHeight }} resizeMode="contain" />
            <View
              style={{
                marginTop: -1,
                paddingHorizontal: isCompact ? 24 : 30,
                paddingTop: isCompact ? 24 : 30,
                paddingBottom: 24,
                backgroundColor: cardBackgroundColor,
              }}
            >
              <Label
                style={{ color: titleColor, fontSize: isCompact ? 27 : 30, lineHeight: isCompact ? 32 : 36, fontWeight: '700' }}
                numberOfLines={4}
              >
                {t('no-nearby-missions-popup-title')}
              </Label>
              <Label style={{ marginTop: 22, color: bodyColor, fontSize: isCompact ? 16 : 17, lineHeight: isCompact ? 22 : 24 }} numberOfLines={8}>
                {t('no-nearby-missions-popup-body')}
              </Label>
              <CustomButton
                title={t('no-nearby-missions-popup-cta')}
                onPress={handleClose}
                style={{ marginTop: 28, width: '100%', minHeight: 52, backgroundColor: buttonColor, borderColor: buttonColor }}
                textStyle={{ color: '#ffffff', fontSize: 15, lineHeight: 19, fontWeight: '700' }}
              />
              <View style={{ marginTop: 18, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                <FontAwesomeIcon icon={faBell} size={30} color={buttonColor} />
                <Label style={{ flexShrink: 1, color: secondaryTextColor, fontSize: 14, lineHeight: 18, fontWeight: '600' }} numberOfLines={3}>
                  {t('no-nearby-missions-popup-secondary')}
                </Label>
              </View>
              <CustomCheckbox
                value="dontShowAgainNoNearbyMissions"
                label={t('Dont-show-this-again')}
                checked={dontShowAgain}
                color={buttonColor}
                markerColor={buttonColor}
                boxStyle={{ backgroundColor: cardBackgroundColor, borderColor: buttonColor }}
                textStyle={{ color: secondaryTextColor, fontSize: 12, lineHeight: 16 }}
                style={{ marginTop: 18, justifyContent: 'center' }}
                onChange={setDontShowAgain}
              />
            </View>
          </ScrollView>
        </Popup>
      </Overlay>
    </Modal>
  );
};

export default NoNearbyMissionsPopup;
